/**
 * CapabilityModel Service - React Query Integration
 * Handles all capability-model mapping API communications
 *
 * NOTE: This service manages the relationship between Capabilities and Models
 * It does NOT contain execution logic - this is purely configuration management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import {
  CapabilityModel,
  CreateCapabilityModelInput,
  UpdateCapabilityModelInput,
} from '../types/admin';
import { AxiosError } from 'axios';
import { useNotification } from '../hooks/useNotification';

// ============================================
// API Response Types
// ============================================

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// ============================================
// Query Keys
// ============================================

export const capabilityModelKeys = {
  all: ['capability-models'] as const,
  lists: () => [...capabilityModelKeys.all, 'list'] as const,
  list: (capabilityPublicId: string) =>
    [...capabilityModelKeys.lists(), capabilityPublicId] as const,
  details: () => [...capabilityModelKeys.all, 'detail'] as const,
  detail: (id: number) => [...capabilityModelKeys.details(), id] as const,
};

// ============================================
// API Functions
// ============================================

/**
 * Fetches all models bound to a specific capability
 * Backend determines the data - UI only displays it
 */
const fetchCapabilityModels = async (capabilityPublicId: string): Promise<CapabilityModel[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<CapabilityModel[]>>(
      `/v1/admin/capabilities/${capabilityPublicId}/models`
    );
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching capability models:', error);
    throw error;
  }
};

/**
 * Creates a new capability-model mapping
 * Backend enforces business rules (e.g., single default model)
 */
const createCapabilityModel = async (
  input: CreateCapabilityModelInput
): Promise<CapabilityModel> => {
  try {
    const response = await axiosInstance.post<ApiResponse<CapabilityModel>>(
      `/v1/admin/capabilities/${input.capabilityPublicId}/models`,
      {
        llmModelPublicId: input.llmModelPublicId,
        providerCode: input.providerCode,
        modelCode: input.modelCode,
        costTier: input.costTier,
        priority: input.priority,
        isDefault: input.isDefault,
        isActive: input.isActive,
        config: input.config,
        inputSchema: input.inputSchema,
        outputSchema: input.outputSchema,
        defaultParams: input.defaultParams,
        lockedParams: input.lockedParams,
        paramMapping: input.paramMapping,
        outputMapping: input.outputMapping,
        paramOverrides: input.paramOverrides,
      }
    );
    return response.data.data as CapabilityModel;
  } catch (error) {
    console.error('Error creating capability model:', error);
    throw error;
  }
};

/**
 * Updates an existing capability-model mapping
 * Backend validates changes (e.g., ensuring one default model)
 */
const updateCapabilityModel = async ({
  id,
  ...input
}: UpdateCapabilityModelInput): Promise<CapabilityModel> => {
  try {
    const response = await axiosInstance.patch<ApiResponse<CapabilityModel>>(
      `/v1/admin/capability-models/${id}`,
      input
    );
    return response.data.data as CapabilityModel;
  } catch (error) {
    console.error('Error updating capability model:', error);
    throw error;
  }
};

/**
 * Deletes a capability-model mapping (soft delete)
 * Backend ensures capability remains in valid state
 */
const deleteCapabilityModel = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/v1/admin/capability-models/${id}`);
  } catch (error) {
    console.error('Error deleting capability model:', error);
    throw error;
  }
};

/**
 * Updates capability-model active status
 * UI shows current state - backend is source of truth
 */
const updateCapabilityModelStatus = async ({
  id,
  isActive,
}: {
  id: number;
  isActive: boolean;
}): Promise<CapabilityModel> => {
  try {
    const response = await axiosInstance.patch<ApiResponse<CapabilityModel>>(
      `/v1/admin/capability-models/${id}/status`,
      { isActive }
    );
    return response.data.data as CapabilityModel;
  } catch (error) {
    console.error('Error updating capability model status:', error);
    throw error;
  }
};

// ============================================
// React Query Hooks
// ============================================

/**
 * Hook to fetch all models for a specific capability
 * Data structure reflects capabilityModel schema in backend
 */
export const useCapabilityModels = (capabilityPublicId: string) => {
  return useQuery({
    queryKey: capabilityModelKeys.list(capabilityPublicId),
    queryFn: () => fetchCapabilityModels(capabilityPublicId),
    enabled: !!capabilityPublicId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to create a new capability-model mapping
 * Backend validates business rules - UI displays results
 */
export const useCreateCapabilityModel = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: createCapabilityModel,
    onSuccess: () => {
      // Invalidate all capability model lists (since we don't have publicId in response)
      queryClient.invalidateQueries({
        queryKey: capabilityModelKeys.lists(),
      });
      // Also invalidate capabilities list (for model count)
      queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      notification.success('Model mapping created successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.error || 'Failed to create model mapping';
      notification.error(message);
      console.error('Error creating capability model:', error);
    },
  });
};

/**
 * Hook to update an existing capability-model mapping
 * Backend enforces constraints (e.g., single default)
 */
export const useUpdateCapabilityModel = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: updateCapabilityModel,
    onSuccess: () => {
      // Invalidate all capability model lists
      queryClient.invalidateQueries({
        queryKey: capabilityModelKeys.lists(),
      });
      notification.success('Model mapping updated successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.error || 'Failed to update model mapping';
      notification.error(message);
      console.error('Error updating capability model:', error);
    },
  });
};

/**
 * Hook to delete a capability-model mapping
 * Backend ensures capability remains valid after deletion
 */
export const useDeleteCapabilityModel = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: deleteCapabilityModel,
    onSuccess: () => {
      // Invalidate all capability model lists
      queryClient.invalidateQueries({ queryKey: capabilityModelKeys.lists() });
      // Also invalidate capabilities list (for model count)
      queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      notification.success('Model mapping deleted successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.error || 'Failed to delete model mapping';
      notification.error(message);
      console.error('Error deleting capability model:', error);
    },
  });
};

/**
 * Hook to toggle capability-model active status with optimistic updates
 * UI validation ≠ business logic; backend is source of truth
 */
export const useToggleCapabilityModelStatus = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: updateCapabilityModelStatus,
    // Optimistic update for better UX
    onMutate: async (variables: { id: number; isActive: boolean }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: capabilityModelKeys.lists() });

      // Snapshot previous value
      const previousData = queryClient.getQueriesData<CapabilityModel[]>({
        queryKey: capabilityModelKeys.lists(),
      });

      // Optimistically update (if data exists)
      queryClient.setQueriesData<CapabilityModel[]>(
        { queryKey: capabilityModelKeys.lists() },
        (old) => {
          if (!old) return old;
          return old.map((model) =>
            model.id === variables.id ? { ...model, isActive: variables.isActive } : model
          );
        }
      );

      return { previousData };
    },
    // Rollback on error
    onError: (
      error: AxiosError<ApiResponse>,
      _variables,
      context: { previousData?: Array<[QueryKey, CapabilityModel[] | undefined]> } | undefined
    ) => {
      if (context?.previousData) {
        // Restore previous state
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      const message = error.response?.data?.error || 'Failed to update model status';
      notification.error(message);
      console.error('Error toggling capability model status:', error);
    },
    // Refetch on success
    onSuccess: (_data, variables) => {
      // Invalidate all capability model lists
      queryClient.invalidateQueries({
        queryKey: capabilityModelKeys.lists(),
      });
      notification.success(
        `Model ${variables.isActive ? 'activated' : 'deactivated'} successfully`
      );
    },
  });
};
